import type {
  DomainClassSummary,
  DomainPropertySummary,
  DomainPropertyIR,
  MetamodelGraphqlMutation,
  MetamodelNotesInput,
} from "@granular-software/metamodel-core";
import {
  defineMetamodelPackage,
  normalizeNotesInput,
} from "@granular-software/metamodel-core";

function noteTexts(values: Array<{ text?: string | null }> | null | undefined) {
  return (values || [])
    .map((item) => item?.text)
    .filter(
      (value): value is string => typeof value === "string" && value.length > 0,
    );
}

function buildNoteMutations(
  targetPath: string,
  notes?: MetamodelNotesInput,
): MetamodelGraphqlMutation[] {
  return normalizeNotesInput(notes).map((note) => ({
    label: `add note on ${targetPath}`,
    query: `mutation { at(path: ${JSON.stringify(targetPath)}) { add_note(text: ${JSON.stringify(
      note,
    )}) { text } } }`,
  }));
}

export function buildFieldNoteMutations(
  fieldPath: string,
  notes?: MetamodelNotesInput,
) {
  return buildNoteMutations(fieldPath, notes);
}

export function buildModelNoteMutations(
  modelPath: string,
  notes?: MetamodelNotesInput,
) {
  return buildNoteMutations(modelPath, notes);
}

export function readClassNotes(
  rawClass: Record<string, any>,
): Partial<DomainClassSummary> {
  return {
    notes: noteTexts(rawClass.notes),
  };
}

export function readPropertyNotes(
  rawProperty: Record<string, any>,
): Partial<DomainPropertySummary> {
  return {
    notes: noteTexts(rawProperty.notes),
  };
}

export const noteMetamodelPackage = defineMetamodelPackage({
  id: "note",
  docs: {
    fieldRows: [
      {
        key: "note",
        description:
          "Advisory text attached to a field. Accepts a string or string array.",
      },
    ],
    modelRows: [
      { key: "note", description: "Advisory text on the class/model itself." },
    ],
  },
  graphql: {
    typeDefs: [
      `
      type NoteMetamodel {
        model: Model!
        text: String!
      }

      extend type Model {
        notes: [NoteMetamodel!]!
      }

      extend type ModelMutation {
        add_note(text: String!): NoteMetamodel
      }
      `,
    ],
    createResolvers({ run }) {
      return {
        NoteMetamodel: {
          model: (value) => value.model,
          text: (value) => value.text,
        },
        Model: {
          notes: async (ant) => await run(ant.notes()),
        },
        ModelMutation: {
          add_note: async (ant, { text }) => {
            const model = await run(ant.add_note(text));
            return { model, text };
          },
        },
      };
    },
  },
  manifest: {
    buildFieldMutations(fieldPath, spec) {
      return buildFieldNoteMutations(fieldPath, spec.note);
    },
    buildModelMutations(modelPath, spec) {
      return buildModelNoteMutations(modelPath, spec.note);
    },
  },
  summary: {
    selections: {
      classFields: [`notes { text }`],
      propertyFields: [`notes { text }`],
    },
    readClassSummary: readClassNotes,
    readPropertySummary: readPropertyNotes,
  },
  domain: {
    applyToClassIR(classIR) {
      return {
        ...classIR,
        docs: [...classIR.docs, ...(classIR.source.notes || [])],
      };
    },
    applyToPropertyIR(
      propertyIR: DomainPropertyIR,
      propertySummary: DomainPropertySummary,
    ) {
      return {
        ...propertyIR,
        docs: [...propertyIR.docs, ...(propertySummary.notes || [])],
      };
    },
  },
});
